import { computed, reactive, watch } from 'vue'
import { useMonthlyConfigurationDepartments } from './useMonthlyConfigurationDepartments.js'
import {
  useMonthlyConfigurationUserSearch,
  runUserSearchImmediately
} from './useMonthlyConfigurationUserSearch.js'
import { ALL_BRANCHES_SCOPE_VALUE } from './useMonthlyConfigurationBranches.js'

export function resolveSelectEntityForScope(selectedBranchId, selectEntity) {
  const scope = String(selectedBranchId || '').trim()
  if (scope === ALL_BRANCHES_SCOPE_VALUE) return 'all'
  return String(selectEntity || '').trim()
}

const form = reactive({
  selectedMonth: '',
  fromDate: '',
  toDate: '',
  selectedBranchId: '',
  selectEntity: '',
  departmentId: '',
  searchName: ''
})

let watchersInitialized = false
let suppressEntityWatchEffects = false
let sharedLoadDepartments = null
let sharedClearUserSearchResults = null

function daysInMonth(month, year = new Date().getFullYear()) {
  const m = parseInt(month, 10)
  if (!m || m < 1 || m > 12) return 0
  return new Date(year, m, 0).getDate()
}

function daysBetweenInclusive(fromDate, toDate) {
  const from = new Date(`${fromDate}T00:00:00`)
  const to = new Date(`${toDate}T00:00:00`)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 0
  const diffMs = to.getTime() - from.getTime()
  return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1
}

export function useMonthlyConfigurationFilters() {
  const { departments, isLoadingDepartments, loadDepartments } = useMonthlyConfigurationDepartments()
  const {
    searchResults,
    isSearchingUsers,
    scheduleUserSearch,
    clearUserSearchResults
  } = useMonthlyConfigurationUserSearch()

  sharedLoadDepartments = loadDepartments
  sharedClearUserSearchResults = clearUserSearchResults

  if (!watchersInitialized) {
    watchersInitialized = true

    watch(
      () => form.selectedMonth,
      (month) => {
        if (month) {
          form.fromDate = ''
          form.toDate = ''
        }
      }
    )

    watch(
      () => [form.fromDate, form.toDate],
      ([fromDate, toDate]) => {
        if (fromDate || toDate) {
          form.selectedMonth = ''
        }
      }
    )

    watch(
      () => form.selectedBranchId,
      () => {
        if (suppressEntityWatchEffects) return
        if (String(form.selectedBranchId) === ALL_BRANCHES_SCOPE_VALUE) {
          form.selectEntity = 'all'
        } else {
          form.selectEntity = ''
        }
        form.departmentId = ''
        form.searchName = ''
        clearUserSearchResults()
        loadDepartments(null)
      }
    )

    watch(
      () => form.selectEntity,
      (entity) => {
        if (suppressEntityWatchEffects) return
        if (entity !== 'by_department') {
          form.departmentId = ''
        }
        if (entity !== 'by_name') {
          form.searchName = ''
          clearUserSearchResults()
        }
        if (entity === 'by_department' && form.selectedBranchId) {
          loadDepartments(form.selectedBranchId)
        }
      },
      { immediate: true }
    )

    watch(
      () => [form.selectEntity, form.searchName, form.selectedBranchId],
      ([entity, name, branchId]) => {
        if (entity !== 'by_name') return
        scheduleUserSearch(name, branchId)
      },
      { immediate: true }
    )

    watch(departments, (rows) => {
      if (isLoadingDepartments.value || suppressEntityWatchEffects) return
      if (form.departmentId && !rows.some((d) => String(d.id) === String(form.departmentId))) {
        form.departmentId = ''
      }
    })
  }

  const showDepartmentSelect = computed(() => form.selectEntity === 'by_department')
  const showSearchByName = computed(() => form.selectEntity === 'by_name')
  const departmentOptions = computed(() => departments.value)

  const totalDays = computed(() => {
    if (form.fromDate && form.toDate) {
      return daysBetweenInclusive(form.fromDate, form.toDate)
    }
    if (form.selectedMonth) {
      return daysInMonth(form.selectedMonth)
    }
    return 0
  })

  return {
    form,
    showDepartmentSelect,
    showSearchByName,
    departmentOptions,
    isLoadingDepartments,
    loadDepartments,
    totalDays,
    searchResults,
    isSearchingUsers
  }
}

export function buildEarnDeductFilterContext(sourceForm = form) {
  return {
    selectedBranchId: sourceForm.selectedBranchId || '',
    selectEntity: resolveSelectEntityForScope(sourceForm.selectedBranchId, sourceForm.selectEntity),
    departmentId: sourceForm.departmentId || '',
    searchName: sourceForm.searchName || ''
  }
}

export function clearFilterEntityFields() {
  suppressEntityWatchEffects = true
  form.selectedBranchId = ''
  form.selectEntity = ''
  form.departmentId = ''
  form.searchName = ''
  suppressEntityWatchEffects = false
  if (sharedClearUserSearchResults) {
    sharedClearUserSearchResults()
  }
  if (sharedLoadDepartments) {
    sharedLoadDepartments(null)
  }
}

export function resetMonthlyConfigurationForm() {
  suppressEntityWatchEffects = true
  form.selectedMonth = ''
  form.fromDate = ''
  form.toDate = ''
  form.selectedBranchId = ''
  form.selectEntity = ''
  form.departmentId = ''
  form.searchName = ''
  suppressEntityWatchEffects = false
  if (sharedClearUserSearchResults) {
    sharedClearUserSearchResults()
  }
  if (sharedLoadDepartments) {
    sharedLoadDepartments(null)
  }
}

export async function applyEarnDeductFilterContext(context = {}) {
  suppressEntityWatchEffects = true
  form.selectedBranchId = context.selectedBranchId ? String(context.selectedBranchId) : ''
  form.selectEntity = resolveSelectEntityForScope(
    form.selectedBranchId,
    context.selectEntity || form.selectEntity
  )
  form.departmentId = context.departmentId ? String(context.departmentId) : ''
  form.searchName = context.searchName || ''

  if (form.selectEntity === 'by_department' && form.selectedBranchId && sharedLoadDepartments) {
    await sharedLoadDepartments(form.selectedBranchId)
  } else if (sharedLoadDepartments) {
    await sharedLoadDepartments(null)
  }

  if (form.selectEntity === 'by_name' && String(form.searchName || '').trim() && form.selectedBranchId) {
    await runUserSearchImmediately(form.searchName, form.selectedBranchId)
  } else if (form.selectEntity !== 'by_name' && sharedClearUserSearchResults) {
    sharedClearUserSearchResults()
  }

  suppressEntityWatchEffects = false
}
